import { useState, useEffect } from 'react';
import clsx from 'clsx';
import { Modal } from './Modal';
import {
  FormInput,
  FormRow,
  FormField,
  ColorPicker,
} from './FormFields';
import { useUIStore } from '../store/uiStore';
import { useShallow } from 'zustand/react/shallow';
import { useLibraryStore } from '../store/libraryStore';
import { useTripStore } from '../store/tripStore';

export function AccommodationConfigModal() {
  const { activeEditor, closeEditor } = useUIStore(
    useShallow((s) => ({ activeEditor: s.activeEditor, closeEditor: s.closeEditor }))
  );
  const isOpen = activeEditor?.type === 'accommodationConfig';
  const poiId = isOpen ? activeEditor.entityId : null;

  const getPOI = useLibraryStore((s) => s.getPOI);
  const getAccommodationConfig = useTripStore((s) => s.getAccommodationConfig);
  const setAccommodationConfig = useTripStore((s) => s.setAccommodationConfig);

  const [colorIndex, setColorIndex] = useState(0);
  const [checkInTime, setCheckInTime] = useState('15:00');
  const [checkOutTime, setCheckOutTime] = useState('11:00');

  // Load existing config when modal opens
  useEffect(() => {
    if (!isOpen || !poiId) return;

    const config = getAccommodationConfig(poiId);
    if (config) {
      setColorIndex(config.color);
      setCheckInTime(config.checkInTime ?? '15:00');
      setCheckOutTime(config.checkOutTime ?? '11:00');
    } else {
      // Default values for new config
      setColorIndex(0);
      setCheckInTime('15:00');
      setCheckOutTime('11:00');
    }
  }, [isOpen, poiId, getAccommodationConfig]);

  const poi = poiId ? getPOI(poiId) : null;
  const poiName = poi?.name ?? 'Accommodation';

  const handleSave = () => {
    if (!poiId) return;

    setAccommodationConfig(poiId, {
      color: colorIndex,
      checkInTime: checkInTime || undefined,
      checkOutTime: checkOutTime || undefined,
    });

    closeEditor();
  };

  return (
    <Modal
      open={isOpen}
      onClose={closeEditor}
      title={`Configure ${poiName}`}
      icon="tune"
      width="400px"
      footer={
        <>
          <button onClick={closeEditor} className="btn-secondary">
            Cancel
          </button>
          <button
            onClick={handleSave}
            className={clsx(
              'px-5 py-2 text-[13px] font-semibold font-sans rounded-lg border-none cursor-pointer transition-all',
              'bg-text-primary text-white'
            )}
          >
            Save
          </button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <p className="text-sm text-text-secondary">
          Configure trip-specific settings for this accommodation. These settings only apply to this trip.
        </p>

        <FormField label="Color Theme">
          <ColorPicker selectedIndex={colorIndex} onChange={setColorIndex} />
        </FormField>

        <FormRow>
          <FormField label="Check-in Time" flex={1}>
            <FormInput value={checkInTime} onChange={setCheckInTime} type="time" />
          </FormField>
          <FormField label="Check-out Time" flex={1}>
            <FormInput value={checkOutTime} onChange={setCheckOutTime} type="time" />
          </FormField>
        </FormRow>
      </div>
    </Modal>
  );
}
